import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Building2, CalendarClock } from "lucide-react";
import api, { fmtEur } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const num = (v) => (v === "" || v == null ? 0 : Number(String(v).replace(",", ".")) || 0);
const oggi = () => new Date().toISOString().slice(0, 10);
const fmtData = (d) => { if (!d) return "—"; const [y, m, g] = d.slice(0, 10).split("-"); return g && m && y ? `${g}/${m}/${y}` : d; };

export default function Banche() {
  const qc = useQueryClient();
  const { data: conti = [] } = useQuery({ queryKey: ["banche"], queryFn: async () => (await api.get("/banche")).data });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["banche"] });

  const addRow = async () => {
    try { await api.post("/banche", { banca: "", numero_conto: "", iban: "", saldo: 0, saldo_aggiornato_al: oggi() }); invalidate(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore creazione"); }
  };
  const patch = async (row, body) => {
    try { await api.put(`/banche/${row.id}`, body); invalidate(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };
  const patchSaldo = (row, v) => { const nv = num(v); if (nv !== num(row.saldo)) patch(row, { saldo: nv, saldo_aggiornato_al: oggi() }); };
  const removeRow = async (id) => { try { await api.delete(`/banche/${id}`); invalidate(); } catch { toast.error("Errore eliminazione"); } };

  const totale = conti.reduce((a, c) => a + num(c.saldo), 0);

  return (
    <div className="space-y-5 max-w-5xl" data-testid="banche-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Building2 size={22} className="text-emerald-600" /> Banche</h1>
          <p className="text-sm text-muted-foreground">Conti correnti e saldi. La data si aggiorna in automatico quando modifichi un saldo.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Totale saldi</div>
            <div className="text-2xl font-head font-bold tabnum text-emerald-700" data-testid="banche-totale">{fmtEur(totale)}</div>
          </div>
          <Button onClick={addRow} className="font-semibold" data-testid="banche-add-btn"><Plus size={16} className="mr-1.5" /> Aggiungi conto</Button>
        </div>
      </div>

      <div className="gr-card p-0 overflow-x-auto" data-testid="banche-table">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b bg-secondary/30">
              <th className="py-2.5 px-3">Banca</th>
              <th className="py-2.5 px-3">Numero conto</th>
              <th className="py-2.5 px-3">IBAN</th>
              <th className="py-2.5 px-3 text-right">Saldo</th>
              <th className="py-2.5 px-3">Aggiornato al</th>
              <th className="w-8 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {conti.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-sm text-muted-foreground italic">Nessun conto. Clicca "Aggiungi conto".</td></tr>}
            {conti.map((row) => (
              <tr key={row.id} className="border-b last:border-0" data-testid={`banche-row-${row.id}`}>
                <td className="py-1.5 px-3">
                  <input defaultValue={row.banca || ""} key={`b-${row.id}-${row.banca}`} onBlur={(e) => { if (e.target.value !== (row.banca || "")) patch(row, { banca: e.target.value }); }}
                    className="w-full min-w-[140px] border rounded-md px-2 py-1.5 text-sm focus:border-primary outline-none" placeholder="Es. Intesa Sanpaolo" data-testid={`banche-banca-${row.id}`} />
                </td>
                <td className="py-1.5 px-3">
                  <input defaultValue={row.numero_conto || ""} key={`n-${row.id}-${row.numero_conto}`} onBlur={(e) => { if (e.target.value !== (row.numero_conto || "")) patch(row, { numero_conto: e.target.value }); }}
                    className="w-full min-w-[120px] border rounded-md px-2 py-1.5 text-sm focus:border-primary outline-none" placeholder="000012345" data-testid={`banche-numero-${row.id}`} />
                </td>
                <td className="py-1.5 px-3">
                  <input defaultValue={row.iban || ""} key={`i-${row.id}-${row.iban}`} onBlur={(e) => { if (e.target.value !== (row.iban || "")) patch(row, { iban: e.target.value.toUpperCase() }); }}
                    className="w-full min-w-[240px] border rounded-md px-2 py-1.5 text-sm font-mono focus:border-primary outline-none" placeholder="IT60X0542811101000000123456" data-testid={`banche-iban-${row.id}`} />
                </td>
                <td className="py-1.5 px-3 text-right">
                  <input type="number" defaultValue={row.saldo ?? 0} key={`s-${row.id}-${row.saldo}`} onBlur={(e) => patchSaldo(row, e.target.value)}
                    className="w-28 border rounded-md px-2 py-1.5 text-sm text-right tabnum font-semibold focus:border-primary outline-none" data-testid={`banche-saldo-${row.id}`} />
                </td>
                <td className="py-1.5 px-3">
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`banche-data-${row.id}`}><CalendarClock size={13} /> {fmtData(row.saldo_aggiornato_al)}</span>
                </td>
                <td className="py-1.5 px-2">
                  <button onClick={() => removeRow(row.id)} className="text-muted-foreground hover:text-rose-600 p-1" data-testid={`banche-del-${row.id}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
          {conti.length > 0 && (
            <tfoot>
              <tr className="border-t-2 font-bold bg-secondary/20">
                <td className="py-2.5 px-3" colSpan={3}>Totale ({conti.length} conti)</td>
                <td className="py-2.5 px-3 text-right tabnum text-emerald-700">{fmtEur(totale)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
